import { Card, Descriptions, Space, Tag, Typography } from 'antd'
import { ReviewSection, ReviewSectionProps } from 'features/reporting/types'
import { FfsReportSectionId } from 'pages/reporting/reports/ffs/ffsReportData'
import { useFfsReportOps } from 'pages/reporting/reports/ffs/FfsReportProvider'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import React, { useMemo } from 'react'

const { Title, Text } = Typography

const RenderReact: React.FC<ReviewSectionProps> = ({ previewMode }) => {
  const {
    formData,
    indicationsWithReports,
    completionStatus,
    lockState,
    handleStartSignoff,
    handleUnlock,
    canStartSignoff,
  } = useFfsReportOps()

  const isComplete = useMemo(() => {
    return Object.values(completionStatus).every((status) => status.isComplete)
  }, [completionStatus])

  const assessmentSummary = useMemo(() => {
    const total = indicationsWithReports.length
    const passing = formData.assessmentResults.filter(
      (r) => r.conclusion.canReachTargetDate === true
    ).length
    const failing = formData.assessmentResults.filter(
      (r) => r.conclusion.canReachTargetDate === false
    ).length
    const pending = total - passing - failing

    return { total, passing, failing, pending }
  }, [indicationsWithReports, formData.assessmentResults])

  if (previewMode) {
    return (
      <div style={{ padding: 16 }}>
        <Title level={4}>Report Preview</Title>
        <Text>Preview mode - view only</Text>
      </div>
    )
  }

  return (
    <StyledForm layout='vertical'>
      <Title level={4}>Report Review</Title>

      {/* Report Status */}
      <Card title='Report Status' style={{ marginBottom: 16 }}>
        <Descriptions column={1}>
          <Descriptions.Item label='Report Title'>
            {formData.coverSectionData.reportTitle}
          </Descriptions.Item>
          <Descriptions.Item label='Customer'>
            {formData.mainInfo.customerName}
          </Descriptions.Item>
          <Descriptions.Item label='Asset'>
            {formData.mainInfo.assetName}
          </Descriptions.Item>
          <Descriptions.Item label='Revision'>
            {formData.revisionMetadata.revisionNumber}
          </Descriptions.Item>
          <Descriptions.Item label='Completion Status'>
            {isComplete ? (
              <Tag color='green'>All Sections Complete</Tag>
            ) : (
              <Tag color='orange'>Sections Incomplete</Tag>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Assessment Summary */}
      <Card title='Assessment Summary' style={{ marginBottom: 16 }}>
        <Space direction='vertical' style={{ width: '100%' }}>
          <Descriptions column={2}>
            <Descriptions.Item label='Total Indications'>
              {assessmentSummary.total}
            </Descriptions.Item>
            <Descriptions.Item label='Passing'>
              <Tag color='green'>{assessmentSummary.passing}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label='Failing'>
              <Tag color='red'>{assessmentSummary.failing}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label='Pending'>
              <Tag color='default'>{assessmentSummary.pending}</Tag>
            </Descriptions.Item>
          </Descriptions>
        </Space>
      </Card>

      {/* Section Status */}
      <Card title='Section Completion' style={{ marginBottom: 16 }}>
        <Space direction='vertical' style={{ width: '100%' }}>
          {Object.entries(completionStatus).map(([sectionId, status]) => (
            <div
              key={sectionId}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: '1px solid #f0f0f0',
              }}>
              <Text>{sectionId}</Text>
              {status.isComplete ? (
                <Tag color='green'>Complete</Tag>
              ) : (
                <Tag color='orange'>
                  {status.completedFields}/{status.totalFields}
                </Tag>
              )}
            </div>
          ))}
        </Space>
      </Card>

      {/* Signoff Status */}
      <Card title='Signoff Status' style={{ marginBottom: 16 }}>
        <Space direction='vertical' style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <Text>Lock Status</Text>
            {lockState.isLocked ? (
              <Tag color='red'>Locked for Signoff</Tag>
            ) : (
              <Tag color='green'>Unlocked</Tag>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <Text>Supervisor Signoff</Text>
            {formData.signoff?.supervisorSignoff ? (
              <Tag color='green'>
                Signed by {formData.signoff.supervisorSignoff.name}
              </Tag>
            ) : (
              <Tag color='default'>Not Signed</Tag>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <Text>Reviewer Signoff</Text>
            {formData.signoff?.reviewerSignoff ? (
              <Tag color='green'>
                Signed by {formData.signoff.reviewerSignoff.name}
              </Tag>
            ) : (
              <Tag color='default'>Not Signed</Tag>
            )}
          </div>
        </Space>
      </Card>

      {/* Actions */}
      <Card title='Actions'>
        <Space>
          {!lockState.isLocked && canStartSignoff && (
            <button onClick={handleStartSignoff}>Start Signoff Process</button>
          )}
          {lockState.isLocked && (
            <button onClick={handleUnlock}>Unlock Report</button>
          )}
        </Space>
      </Card>
    </StyledForm>
  )
}

export const ReportReview: ReviewSection = {
  RenderReact,
  required: true,
  id: FfsReportSectionId.REVIEW,
  title: 'Review',
  description: 'Review report data and complete sign off',
}
